import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { plainMap } from '@ipfabric/utils/fp/plainMap';
import { pipe } from 'fp-ts/lib/function';
import { useCallback, useRef } from 'react';

type CallbackEntry<TCallback> = { callback: TCallback; used: boolean };

/**
 * Generate a list of callbacks with stable identities, usually passed to elements rendered in a loop.
 *
 * Example:
 * ```
 * const Item = memo(props => (
 *   <div>
 *     ...
 *     <Button onClick={props.onRemove(false)}>Normal remove</Button>
 *     <Button onClick={props.onRemove(true)}>Force remove</Button>
 *   </div>
 * ));
 *
 * const handleRemoveItem = useStableCallbackCollection((id: string) => (force: boolean) => { ... });
 *
 * return (
 *   <>
 *     {Object.entries(items).map(([key, item]) => <Item item={item} onRemove={handleRemoveItem(key)} />)}
 *   </>
 * );
 * ```
 *
 * handleRemoveItem(key) provides function with stable identity for each individual key so Item can be
 * easily memoed.
 *
 * The main callback's identity (handleRemoveItem) is also stable.
 *
 * On every render, the hook purges functions under keys that weren't unused in last render to prevent
 * memory leaks. This means that when an item in the list is unmounted and remounted later,
 * the callback for that item will lose its original identity. But in that case there is no memoization
 * of that component anyway.
 */
export function useStableCallbackCollection<
  TKey,
  TCallback extends (...args: any[]) => any,
>(makeCallback: (key: TKey) => TCallback): (key: TKey) => TCallback {
  const stableMakeCallback = useStableCallback(makeCallback);

  const callbacksMapRef = useRef(new Map<TKey, CallbackEntry<TCallback>>());
  // clear unused entries from previous render
  pipe(
    callbacksMapRef.current,
    plainMap.filterMutable(({ used }) => used),
  );
  // mark remaining fields unused
  // biome-ignore lint/complexity/noForEach: map, not array
  callbacksMapRef.current.forEach((entry) => {
    entry.used = false;
  });

  return useCallback((key) => {
    const mapEntry = pipe(
      callbacksMapRef.current,
      plainMap.getOrCreate<TKey, CallbackEntry<TCallback>>(key, () => ({
        // we can't wrap the whole return value to `useStableCallback` and just call `makeCallback`
        // here, because we don't actually call it there, but just store it to the memoized map.
        // Callback stored that way would stop getting updates of `makeCallback` from now on.
        // This is also the reason why we can't share code with a very similar `useMemoCollection` hook.
        callback: ((...args) => stableMakeCallback(key)(...args)) as TCallback,
        used: true,
      })),
    );
    mapEntry.used = true;
    return mapEntry.callback;
  }, []);
}
